import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import MenuItemWithDialog from '../../../../Shared/Table/widgets/TopBar/MenuItem/MenuItemWithDialog';
import RoundedButton from '../../../../Shared/RoundedButton';
import useMetadataUpdate from '../../../../../hooks/useMetadataUpdate';
import { useMutation } from 'react-fetching-library';
import { getEndpoint } from '../../../../../helpers/endpoint';
import showServerError from '../../../../../helpers/showError';
import {
  getTransitionWithVariable,
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  updateCanShowShimmer,
} from '../../../../../redux_store';
import { orderStatusesActions } from '../../../constants';

const useStyles = makeStyles((theme) => ({
  description: {
    maxWidth: 300,
    paddingBottom: 12,
  },
  option: {
    justifyContent: 'flex-start',
  },
  icon: {
    width: 20,
    height: 20,
    fontSize: 16,
    color: 'black',
  },

  title: {
    color: 'black',
    marginLeft: 8,
  },
}));

const TableMenuOptions = ({
                            filter,
                            sort,
                            options,
                            totalCount,
                            setAnchorElMulti,
                            accountUuid,
                            reloadData,
                          }) => {
  const classes = useStyles();
  const { en: { tables: tTables, notifications, errors } } = useSelector((state) => state.i18n);
  const [isOpenDialog, setOpenDialog] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const dispatch = useDispatch();

  const handleCloseDialog = () => {
    setAnchorElMulti(null);
    setOpenDialog(false);
  };

  const hasFilters = useMemo(
    () => !!filter.reduce((accumulator, { value }) => {
      return accumulator + value.toString().replaceAll(' ', '').length;
    }, 0),
    [filter],
  );

  const statusChangeAction = (endpoint, values) => {
    return {
      method: 'POST',
      endpoint: endpoint,
      body: values,
    };
  };

  const { mutate, loading: isStatusLoading } = useMetadataUpdate(useMutation, [statusChangeAction]);

  const handleStatusChange = async (newStatus) => {
    const statusItem = orderStatusesActions.find(
      (orderStatus) => orderStatus.value === newStatus,
    );

    let newEndpoint = getEndpoint(`/accounts/${accountUuid}/orders/actions`, {}, sort, filter);

    const { status, error, payload } = await mutate(newEndpoint, {
        statusAction: statusItem.value,
        totalCount: totalCount,
      },
    );

    const notificationParam = 'order'.charAt(0).toUpperCase() + 'order'.slice(1);

    if (error) {
      switch (status) {
        case 422: {
          if (payload.validationErrors) {
            let result = '';
            result = Object.values(payload.validationErrors).join('. \n').replaceAll(',.', '.').replaceAll('..', '.');
            showServerError(dispatch, result);
            break;
          }
          showServerError(dispatch, errors.validationError);
          break;
        }
        default: {
          showServerError(dispatch, errors.unknownError);
          break;
        }
      }
      handleCloseDialog();
      setIsFailed(true);

      setTimeout(() => {
        setIsFailed(false);
      }, 3000);
    } else {
      dispatch(
        enqueueSnackbar({
          message: getTransitionWithVariable(notifications.orderStatusChanged, {
            name: notificationParam,
          }),
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
      setIsSuccess(true);
      dispatch(updateCanShowShimmer(true));
      setTimeout(() => {
        reloadData(true);
        handleCloseDialog();
      }, 200);

      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    }
  };

  return <>
    <Typography variant='body2' color={'secondary'} className={classes.description}>
      {hasFilters ? tTables.miltiActionFilteredData : tTables.miltiActionAllData}
    </Typography>
    <>
      {
        options.map((props) => {
          const { children, icon, value } = props;
          return <MenuItemWithDialog
            icon={<div className={classes.icon}>{icon}</div>}
            title={<div className={classes.title}>
              {children}
            </div>}
            onClick={() => {
              setOpenDialog(children);
            }}
            buttonAlign={'flex-start'}
            dialogTitle={tTables.changeStatus}
            dialogSubtitle={hasFilters ? tTables.miltiActionFilteredData : tTables.miltiActionAllData}
            dialogIsOpen={isOpenDialog === children}
            dialogHandleClose={handleCloseDialog}
            dialogOptions={[
              {
                label: tTables.status,
                value: children,
              },
              {
                label: tTables.totalNumberOfSelected,
                value: totalCount,
              },
            ]}
            dialogActions={<RoundedButton
              onClick={() => handleStatusChange(value)}
              isLoading={isStatusLoading}
              disabled={isStatusLoading}
              isSuccess={isSuccess}
              isFailed={isFailed}
              variant='contained'
              color='primary'
              size='small'
              className={classes.btn}
              LoadingWrapperProps={{
                LoaderProps: {
                  disableShrink: true,
                },
              }}
            >
              {tTables.change}
            </RoundedButton>}
          />;
        })
      }
    </>
  </>;
};

export default TableMenuOptions;
